import { useMemo } from 'react'
import { TraceKey } from 'src/Libs/system-charts/shared/config/traceConf'
import { useTypedSelector } from '../../../../State/RootReducer'
import { DISCHARGE_CAPABLE_WALLBOX_TYPES } from 'src/Libs/wallboxes/consts'
import { useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'

/**
 * This function returns all possible trace lines for a chart including powermeter and wallbox traces with ids
 */
const useAvailableLines = (): TraceKey[] => {
  const storeData = useTypedSelector(store => store.storage.storageData)
  const { data: wallboxes } = useWallboxHardwareByStorage()
  const powermeters = useTypedSelector(store => store.storage.powermeterHardware)

  return useMemo(() => {
    const _availableLineKeys: TraceKey[] = [
      'ASOC',
      'ForecastPower',
      'DC_PowerSolar',
      'BatPowerIn',
      'BatPowerOut',
      'NetIn',
      'NetOut',
      'DC_PowerL1',
      'DC_PowerL2',
      'DC_PowerL3',
    ]

    if (powermeters?.producers.length || powermeters?.bypassNoCharge.length) {
      _availableLineKeys.push('Powermeter_production')
      _availableLineKeys.push('DC_PowerTotal')

      powermeters.producers.forEach(powermeter => {
        _availableLineKeys.push(`pm_${powermeter.serialno}_${powermeter.id}/Powermeter_production` as TraceKey)
      })
      powermeters.bypassNoCharge.forEach(powermeter => {
        _availableLineKeys.push(`pm_${powermeter.serialno}_${powermeter.id}/Powermeter_NetIn_bypass_noCharge` as TraceKey)
      })
      powermeters.bypassRegulation.forEach(powermeter => {
        _availableLineKeys.push(`pm_${powermeter.serialno}_${powermeter.id}/Powermeter_NetIn_bypass` as TraceKey)
      })
      powermeters.enera.forEach(powermeter => {
        _availableLineKeys.push(`pm_${powermeter.serialno}_${powermeter.id}/Powermeter_NetIn_enera` as TraceKey)
      })
    }
    _availableLineKeys.push('ConsumptionHouse')
    if (powermeters?.consumers.length) _availableLineKeys.push('Powermeter_consumption')

    if (wallboxes?.length) {
      _availableLineKeys.push('Wallbox_energyAll')
      _availableLineKeys.push('Wallbox_energySolar')
      _availableLineKeys.push('Wallbox_energyNetOut')
      if (wallboxes.find(wallbox => DISCHARGE_CAPABLE_WALLBOX_TYPES.includes(wallbox.wallboxType)))
        _availableLineKeys.push('Wallbox_energyDischarged')
      wallboxes.forEach(wallbox => {
        _availableLineKeys.push(
          `wb${wallbox.number}/Wallbox_energyAll` as TraceKey,
          `wb${wallbox.number}/Wallbox_energySolar` as TraceKey,
          `wb${wallbox.number}/Wallbox_energyNetOut` as TraceKey
        )
        if (DISCHARGE_CAPABLE_WALLBOX_TYPES.includes(wallbox.wallboxType))
          _availableLineKeys.push(`wb${wallbox.number}/Wallbox_energyDischarged` as TraceKey)
      })
    }

    if (powermeters?.consumers.length || wallboxes?.length) {
      _availableLineKeys.push('ConsumptionTotal')
      powermeters.consumers.forEach(powermeter => {
        _availableLineKeys.push(`pm_${powermeter.serialno}_${powermeter.id}/Powermeter_consumption` as TraceKey)
      })
    }
    //todo look for powermeters with those types
    // if (systemDataHandler.powermetersByType.bypass.length) _availableLineKeys.push('Powermeter_NetIn_bypass')
    // if (systemDataHandler.powermetersByType.enera.length) _availableLineKeys.push('Powermeter_NetIn_enera')
    if (storeData?.derateAtPower) _availableLineKeys.push('Deration')

    //#endregion

    return _availableLineKeys
  }, [storeData, wallboxes, powermeters])
}

export default useAvailableLines
